import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';
import PlusIcon from '@heroicons/react/24/solid/PlusIcon';
import InformationCircleIcon from '@heroicons/react/24/solid/InformationCircleIcon';
import { FunctionComponent, ReactNode } from 'react';
import { WidgetManifestEntry } from '@/models/widget';
import { APP_BAR_HEIGHT_VAR } from '@/layouts/fullScreen/FullScreenLayout';

interface WidgetListCardProps {
    widgets: WidgetManifestEntry[];
    isLoading: boolean;
    isError: boolean;
    /** Re-runs the manifest query after a failed load. */
    onRetry: () => void;
    onAddWidget: (widget: WidgetManifestEntry) => void;
    /** Opens the documentation dialog for a single widget. */
    onShowDocs: (widget: WidgetManifestEntry) => void;
    /** Rendered in the header beside the title: the list below belongs to the chosen version. */
    versionSelect?: ReactNode;
}

/**
 * Gap between the sticky app bar and the pinned card, matching the page's own
 * vertical padding. `64px` fallback covers a first paint before the layout has
 * measured the bar.
 */
const STICKY_OFFSET = `calc(var(${APP_BAR_HEIGHT_VAR}, 64px) + 32px)`;

/**
 * The rail listing every available widget; a click adds one.
 *
 * On `md` and up it sticks below the app bar while the (much taller) right
 * column scrolls. The card is content-height — the parent grid sets
 * `alignItems: flex-start` — so the sticky box has room to travel inside its
 * grid area. If the list itself outgrows the viewport it scrolls internally
 * instead of having its bottom entries pushed out of reach.
 */
export const WidgetListCard: FunctionComponent<WidgetListCardProps> = ({
    widgets,
    isLoading,
    isError,
    onRetry,
    onAddWidget,
    onShowDocs,
    versionSelect,
}) => (
    <Card
        sx={{
            position: { md: 'sticky' },
            top: { md: STICKY_OFFSET },
            maxHeight: { md: `calc(100vh - ${STICKY_OFFSET} - 32px)` },
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <CardContent sx={{ flexShrink: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant='h6'>Widgets</Typography>
                <Box
                    sx={{
                        width: 100,
                    }}
                >
                    {versionSelect}
                </Box>
            </Box>
        </CardContent>
        <Divider sx={{ width: '100%', flexShrink: 0 }} />

        {isLoading ? (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 4,
                }}
            >
                <CircularProgress size={24} />
            </Box>
        ) : isError ? (
            <Box sx={{ p: 2 }}>
                <Typography color='error' variant='body2' sx={{ mb: 2 }}>
                    Widgets konnten nicht geladen werden.
                </Typography>
                <Button size='small' variant='outlined' onClick={onRetry}>
                    Erneut versuchen
                </Button>
            </Box>
        ) : widgets.length === 0 ? (
            <Box sx={{ p: 2 }}>
                <Typography color='text.secondary' variant='body2'>
                    Keine Widgets verfügbar.
                </Typography>
            </Box>
        ) : (
            <List disablePadding sx={{ overflowY: 'auto' }}>
                {widgets.map((widget) => {
                    const label = widget.label.de;

                    return (
                        /*
                         * Both icons are the list item's `secondaryAction`, i.e.
                         * siblings of the row button rather than children of it:
                         * a button nested inside a button is invalid markup and
                         * would make a click on an icon also add the widget.
                         */
                        <ListItem
                            key={widget.tag}
                            disablePadding
                            secondaryAction={
                                <Stack direction='row' spacing={0.5}>
                                    <IconButton
                                        size='small'
                                        aria-label={`Dokumentation zu ${label}`}
                                        onClick={() => onShowDocs(widget)}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <InformationCircleIcon />
                                        </SvgIcon>
                                    </IconButton>
                                    <IconButton
                                        size='small'
                                        aria-label={`${label} hinzufügen`}
                                        onClick={() => onAddWidget(widget)}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <PlusIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Stack>
                            }
                        >
                            {/*
                             * The row still adds the widget, but without a
                             * background on hover: the two icon buttons keep
                             * their own hover circles, and a full-width fill
                             * behind them made those read as decoration rather
                             * than as the controls they are.
                             */}
                            <ListItemButton
                                onClick={() => onAddWidget(widget)}
                                // Room for the secondary action, which overlays
                                // the row's own right edge.
                                sx={{
                                    pr: 11,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={label}
                                    secondary={widget.tag}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        )}
    </Card>
);
